import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { canSeeRevenue } from '../lib/perms';
import {
  jobsApi,
  applicationsApi,
  candidatesApi,
  commandCenterApi,
  getErrorMessage,
} from '../services/api';
import UserAvatar from '../components/UserAvatar';
import OfferVsJoiningCard from '../components/analytics/OfferVsJoiningCard';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Search,
  Briefcase,
  Building2,
  User,
  Sparkles,
  Loader2,
  FileText,
  Calendar,
  AlertTriangle,
  Flame,
  Clock,
  Target,
  X,
  ExternalLink,
  StickyNote,
  XCircle,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Check,
  Pencil,
  Send,
  TrendingUp,
  Users,
  CheckCircle2,
  Award,
  Activity as ActivityIcon,
  ListTodo,
  IndianRupee,
  CalendarClock,
  Info,
  SlidersHorizontal,
  GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import { List as VirtualList } from 'react-window';
import BrokenResumesBanner from '../components/BrokenResumesBanner';
import GoalBanner from '../components/GoalBanner';
import TodaysWinBanner from '../components/TodaysWinBanner';
import usePersistedState from '../hooks/usePersistedState';
import {
  VIRTUALIZE_THRESHOLD,
  ROW_HEIGHT,
  HIGHLIGHT_DURATION_MS,
  DRILLABLE_METRICS,
  DRILL_TITLES,
  STAGES,
  STAGE_BY_ID,
  formatINR,
  tempBadge,
  getCandidateTemperature,
} from '../components/command-center/constants';
import KanbanCard from '../components/command-center/KanbanCard';
import VirtualKanbanRow from '../components/command-center/VirtualKanbanRow';
import DetailField from '../components/command-center/DetailField';
import EditableDetailField from '../components/command-center/EditableDetailField';
import CompensationLadder from '../components/common/CompensationLadder';
import OwnerLensToggle, { useStickyOwnerLens } from '../components/common/OwnerLensToggle';
import JobsCombobox from '../components/command-center/JobsCombobox';
import StageDetailList from '../components/command-center/StageDetailList';


const CommandCenter = () => {
  const { user } = useAuth();
  const showRevenue = canSeeRevenue(user);
  const isManager = ['admin', 'delivery_manager', 'team_lead'].includes(user?.role);

  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState('all');
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  // Ownership lens — manager-only, persisted across Pipeline ↔ Command
  // Center via shared localStorage key. Recruiters never see the toggle
  // and the backend ignores `owner_lens` for their role.
  const [ownerLens, setOwnerLens] = useStickyOwnerLens('owner_lens', 'created');

  // Wave 2 — Live metrics, daily tasks, recent activity
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [dailyTasks, setDailyTasks] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  // Drill-down: clicking a metric tile opens a dialog with the underlying records
  const [drillMetric, setDrillMetric] = useState(null); // 'submissions' | 'interviews' | ...
  const [drillItems, setDrillItems] = useState([]);
  const [drillSummary, setDrillSummary] = useState({});
  const [drillLoading, setDrillLoading] = useState(false);

  // Stage tabs — switch between kanban ('all') and a rich detail list per stage
  const [activeStageTab, setActiveStageTab] = useState('all');
  const [stageList, setStageList] = useState([]);
  const [stageListLoading, setStageListLoading] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  // Bulk action state — { mode: 'move'|'reject', target_stage: string }
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkRemarks, setBulkRemarks] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Wave 2 — Best 10 candidates AI shortlist dialog
  const [bestDialogJob, setBestDialogJob] = useState(null); // job object or null
  const [bestLoading, setBestLoading] = useState(false);
  const [bestMatches, setBestMatches] = useState([]);
  const [bestSelected, setBestSelected] = useState({}); // { [candidate_id]: true }
  const [bestSubmitting, setBestSubmitting] = useState(false);

  // Wave 2 — Bottom tabs (per-candidate) — last-chosen tab persists per user
  const userKey = user?.id || 'guest';
  const [bottomTab, setBottomTab] = usePersistedState(
    `cc_pref_${userKey}_bottomTab`,
    'activity'
  ); // activity | notes | tasks | history
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // UX polish — collapsible right-panel sub-sections (Resume / Latest Remark) — persist per user
  const [showResume, setShowResume] = usePersistedState(
    `cc_pref_${userKey}_showResume`,
    true
  );
  const [showRemark, setShowRemark] = usePersistedState(
    `cc_pref_${userKey}_showRemark`,
    true
  );

  // Resume preview state — uses blob fetch so the auth header travels with the
  // request and we can show a friendly toast on 404 (broken refs from legacy data).
  const [resumeOpening, setResumeOpening] = useState(false);

  const handleOpenResume = async () => {
    if (!selectedApp?.candidate_id || resumeOpening) return;
    setResumeOpening(true);
    try {
      const res = await candidatesApi.getResume(selectedApp.candidate_id);
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank');
      // Revoke after a short delay so the new tab has time to load
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        toast.error(
          'Resume file is missing. An admin can clean up broken links from Settings → Admin Data Maintenance, or re-upload via the Candidates page.'
        );
      } else if (status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error(getErrorMessage(err, 'Could not open resume'));
      }
    } finally {
      setResumeOpening(false);
    }
  };

  // Right panel state
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [candidateDetail, setCandidateDetail] = useState(null);
  const [candidateLoading, setCandidateLoading] = useState(false);
  // All applications for the selected candidate — feeds the Compensation
  // Ladder so it can pull `offered_ctc` from any past offer/joined apps
  // (not just the currently-selected one).
  const [candidateApps, setCandidateApps] = useState([]);
  const [matchScore, setMatchScore] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);

  // Mobile tab switcher (only used below md breakpoint)
  const [mobileTab, setMobileTab] = useState('pipeline'); // 'jobs' | 'pipeline' | 'candidate'

  // Search-jump highlight state + per-column virtualized list refs
  const [highlightedAppId, setHighlightedAppId] = useState(null);
  const [matchIndex, setMatchIndex] = useState(null); // null = not yet navigated
  const listRefs = useRef({}); // { [stageId]: ListImperativeAPI }
  const highlightTimerRef = useRef(null);

  // Drag & confirm-stage-change state
  const [dragAppId, setDragAppId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [pendingStageChange, setPendingStageChange] = useState(null); // { app, targetStage }
  const [droppedReason, setDroppedReason] = useState('');
  const [droppedReasonText, setDroppedReasonText] = useState('');
  const [stageRemark, setStageRemark] = useState('');
  const [clientFeedback, setClientFeedback] = useState('');
  // Offered CTC captured when transitioning to JOINED. Defaulted on open from
  // the candidate's expected_ctc / current_ctc; recruiter can adjust.
  const [offeredCtc, setOfferedCtc] = useState('');
  // Joining date — mandatory when transitioning to JOINED.
  const [joiningDate, setJoiningDate] = useState('');
  const [savingStage, setSavingStage] = useState(false);

  // Mobile tab auto-switch helpers
  const handleSelectJob = (jobId) => {
    setSelectedJobId(jobId);
    setMobileTab('pipeline'); // jump to board after picking a job on mobile
  };
  const handleSelectApp = (appId) => {
    setSelectedAppId(appId);
    setMobileTab('candidate'); // jump to detail panel on mobile
  };

  // Cleanup highlight timer
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const selectedApp = useMemo(() => {
    if (!selectedAppId) return null;
    // Look up across both data sources so clicks from the rich stage list
    // resolve even when the app isn't in the paginated Kanban window.
    //  - `stageList` (server endpoint) uses `application_id` as the row id
    //    and carries `candidate_name`, `job_title`, etc. directly.
    //  - `applications` (Kanban) uses `id` and is the historical source.
    // Without the fallback, clicking any row in the stage detail list whose
    // app sits past the Kanban's page_size cap (200) would leave the right
    // detail panel showing "Select a candidate card to see details here".
    const fromKanban = applications.find((a) => a.id === selectedAppId);
    if (fromKanban) return fromKanban;
    const fromStage = stageList.find((s) => s.application_id === selectedAppId);
    if (!fromStage) return null;
    // Normalize the stage-list shape into the same field names the right
    // panel reads from. Anything missing (e.g. stage_history) is left
    // undefined — the panel already guards each conditional render.
    return {
      id: fromStage.application_id,
      candidate_id: fromStage.candidate_id,
      candidate_name: fromStage.candidate_name,
      job_id: fromStage.job_id,
      job_title: fromStage.job_title,
      client_name: fromStage.client_name,
      stage: fromStage.stage,
      created_at: fromStage.created_at,
      current_designation: fromStage.current_designation,
      experience_years: fromStage.experience_years,
      expected_ctc: fromStage.expected_ctc,
      current_ctc: fromStage.current_ctc,
      notice_period: fromStage.notice_period,
      location: fromStage.location,
      email: fromStage.email,
      phone: fromStage.phone,
      has_resume: fromStage.has_resume,
      remarks: fromStage.last_activity_remark,
    };
  }, [applications, stageList, selectedAppId]);

  // ---------- Fetch jobs ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setJobsLoading(true);
      try {
        const res = await jobsApi.getAll();
        const items = res.data?.items || (Array.isArray(res.data) ? res.data : []);
        if (!cancelled) setJobs(items);
      } catch (e) {
        if (!cancelled) toast.error(getErrorMessage(e, 'Failed to load jobs'));
      } finally {
        if (!cancelled) setJobsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- Fetch applications (filtered by job) ----------
  const fetchApplications = useCallback(async () => {
    setAppsLoading(true);
    try {
      const params = { page_size: 200 };
      if (selectedJobId && selectedJobId !== 'all') params.job_id = selectedJobId;
      if (isManager) params.owner_lens = ownerLens;
      const res = await applicationsApi.getAll(params);
      const items = res.data?.items || (Array.isArray(res.data) ? res.data : []);
      setApplications(items);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load pipeline'));
    } finally {
      setAppsLoading(false);
    }
  }, [selectedJobId, isManager, ownerLens]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // ---------- Wave 2: Fetch live metrics, daily tasks, recent activity ----------
  // Optional custom range — when set, KPI tiles re-scope to that window.
  const _ccToday = new Date();
  const _ccMonthStart = new Date(_ccToday.getFullYear(), _ccToday.getMonth(), 1);
  const _ccFmt = (d) => d.toISOString().slice(0, 10);
  const [ccRangeActive, setCcRangeActive] = useState(false);
  const [ccStart, setCcStart] = useState(_ccFmt(_ccMonthStart));
  const [ccEnd, setCcEnd] = useState(_ccFmt(_ccToday));

  const fetchCommandCenterData = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const metricsParams =
        ccRangeActive && ccStart && ccEnd && ccStart <= ccEnd
          ? { start_date: ccStart, end_date: ccEnd }
          : {};
      if (isManager) metricsParams.owner_lens = ownerLens;
      // Same lens flows into daily-tasks + recent-activity so every
      // panel below the kanban respects the active scope. Backend
      // hard-ignores the param for recruiters, but we still gate
      // sending it for cleanliness.
      const sideParams = isManager ? { owner_lens: ownerLens } : {};
      const [m, t, a] = await Promise.all([
        commandCenterApi.getMetrics(metricsParams),
        commandCenterApi.getDailyTasks(sideParams),
        commandCenterApi.getRecentActivity(50, sideParams),
      ]);
      setMetrics(m.data || null);
      setDailyTasks(t.data?.tasks || []);
      setRecentActivity(a.data?.items || []);
    } catch (e) {
      // Silent fail — top strip falls back to placeholders
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [ccRangeActive, ccStart, ccEnd, isManager, ownerLens]);

  useEffect(() => {
    fetchCommandCenterData();
    const id = setInterval(fetchCommandCenterData, 60000); // refresh every 60s
    return () => clearInterval(id);
  }, [fetchCommandCenterData]);

  // ---------- Fetch candidate detail when right panel opens ----------
  useEffect(() => {
    if (!selectedApp?.candidate_id) {
      setCandidateDetail(null);
      setCandidateApps([]);
      setMatchScore(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setCandidateLoading(true);
      try {
        // Fetch profile + all applications in parallel so the Compensation
        // Ladder lights up immediately on panel open (it needs offered_ctc
        // from past offer/joined apps, not just the currently selected one).
        const [profileRes, appsRes] = await Promise.all([
          candidatesApi.getById(selectedApp.candidate_id),
          candidatesApi
            .applicationsSummary(selectedApp.candidate_id)
            .catch(() => ({ data: { applications: [] } })),
        ]);
        if (!cancelled) {
          setCandidateDetail(profileRes.data);
          setCandidateApps(appsRes?.data?.applications || []);
        }
      } catch (e) {
        if (!cancelled) {
          setCandidateDetail(null);
          setCandidateApps([]);
          toast.error(getErrorMessage(e, 'Failed to load candidate'));
        }
      } finally {
        if (!cancelled) setCandidateLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedApp?.candidate_id]);

  const handleAiMatch = async () => {
    if (!selectedApp?.candidate_id || !selectedApp?.job_id) return;
    setMatchLoading(true);
    try {
      const res = await candidatesApi.matchToJob(
        selectedApp.candidate_id,
        selectedApp.job_id
      );
      const data = res.data?.analysis || res.data;
      setMatchScore(data);
    } catch (e) {
      setMatchScore(null);
      toast.error(getErrorMessage(e, 'AI matching failed'));
    } finally {
      setMatchLoading(false);
    }
  };

  // ---------- Inline edit of candidate detail fields (RBAC-enforced server-side) ----------
  const saveCandidateField = useCallback(async (field, value) => {
    if (!candidateDetail) return;
    const prev = candidateDetail;
    // Optimistic update
    setCandidateDetail({ ...candidateDetail, [field]: value });
    try {
      const res = await candidatesApi.workbenchUpdate(candidateDetail.id, { [field]: value });
      // Trust backend response so any server-side coercion (e.g. float conversion) is reflected
      if (res.data) {
        setCandidateDetail((cur) => (cur && cur.id === res.data.id ? { ...cur, ...res.data } : cur));
      }
      toast.success('Saved');
    } catch (err) {
      setCandidateDetail(prev);
      toast.error(getErrorMessage(err, 'Could not save change'));
      throw err; // let the field component know it failed
    }
  }, [candidateDetail]);

  const saveOfferedCtc = useCallback(async (_field, value) => {
    // Field lives on the application doc, not the candidate. User types in
    // LPA (e.g. 12.5); we persist in rupees so all aggregations share the
    // same unit. Empty value clears.
    if (!selectedApp) return;
    const prevApp = selectedApp;
    const rupees = value == null || value === '' ? null : Math.round(Number(value) * 100000);
    // Optimistic UI: update the application list so selectedApp (a useMemo
    // derived from `applications`) recomputes with the new value.
    setApplications((prev) =>
      prev.map((a) => (a.id === selectedApp.id ? { ...a, offered_ctc: rupees } : a))
    );
    try {
      if (rupees == null) {
        // Backend rejects null/0; we treat clear as a no-op for now.
        return;
      }
      await commandCenterApi.setApplicationOfferedCtc(selectedApp.id, rupees);
      toast.success('Offered CTC saved');
    } catch (err) {
      // Rollback on failure
      setApplications((prev) =>
        prev.map((a) => (a.id === prevApp.id ? prevApp : a))
      );
      toast.error(getErrorMessage(err, 'Could not save Offered CTC'));
      throw err;
    }
  }, [selectedApp]);

  const saveJoiningDate = useCallback(async (_field, value) => {
    // Stored on the application doc. Empty value clears the date.
    if (!selectedApp) return;
    const prevApp = selectedApp;
    const jd = value == null || value === '' ? null : String(value);
    // Optimistic update via applications list (selectedApp is a useMemo).
    setApplications((prev) =>
      prev.map((a) => (a.id === selectedApp.id ? { ...a, joining_date: jd } : a))
    );
    try {
      await commandCenterApi.setApplicationJoiningDate(selectedApp.id, jd);
      toast.success(jd ? 'Joining date saved' : 'Joining date cleared');
    } catch (err) {
      setApplications((prev) =>
        prev.map((a) => (a.id === prevApp.id ? prevApp : a))
      );
      toast.error(getErrorMessage(err, 'Could not save joining date'));
      throw err;
    }
  }, [selectedApp]);

  const saveOfferLetterDate = useCallback(async (_field, value) => {
    // Manual offer letter date (the date on the offer letter PDF). Different
    // from offer_released_at which is the auto-stamped stage-change time.
    if (!selectedApp) return;
    const prevApp = selectedApp;
    const old = value == null || value === '' ? null : String(value);
    setApplications((prev) =>
      prev.map((a) => (a.id === selectedApp.id ? { ...a, offer_letter_date: old } : a))
    );
    try {
      await commandCenterApi.setApplicationOfferLetterDate(selectedApp.id, old);
      toast.success(old ? 'Offer letter date saved' : 'Offer letter date cleared');
    } catch (err) {
      setApplications((prev) =>
        prev.map((a) => (a.id === prevApp.id ? prevApp : a))
      );
      toast.error(getErrorMessage(err, 'Could not save offer letter date'));
      throw err;
    }
  }, [selectedApp]);

  // ---------- Wave 2: Best 10 candidates AI shortlist ----------
  const openBestCandidatesDialog = async (job) => {
    setBestDialogJob(job);
    setBestMatches([]);
    setBestSelected({});
    setBestLoading(true);
    try {
      const res = await jobsApi.quickMatchCandidates(job.id, 10);
      const items = res.data?.matches || [];
      setBestMatches(items);
      // Pre-select top 5 by default
      const preSel = {};
      items.slice(0, 5).forEach((m) => {
        preSel[m.candidate_id] = true;
      });
      setBestSelected(preSel);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not load best candidates'));
    } finally {
      setBestLoading(false);
    }
  };

  const closeBestDialog = () => {
    if (bestSubmitting) return;
    setBestDialogJob(null);
    setBestMatches([]);
    setBestSelected({});
  };

  const submitBestShortlist = async () => {
    if (!bestDialogJob) return;
    const ids = Object.keys(bestSelected).filter((k) => bestSelected[k]);
    if (ids.length === 0) {
      toast.error('Select at least one candidate');
      return;
    }
    setBestSubmitting(true);
    try {
      const res = await applicationsApi.bulkShortlist({
        job_id: bestDialogJob.id,
        candidate_ids: ids,
        stage: 'sourced',
        remarks: 'Added via AI Best 10 shortlist',
      });
      const r = res.data || {};
      toast.success(
        `Added ${r.successful || 0} candidate${(r.successful || 0) === 1 ? '' : 's'}` +
          (r.skipped ? ` (skipped ${r.skipped} already in pipeline)` : '')
      );
      closeBestDialog();
      // Switch the kanban to that job + refresh
      setSelectedJobId(bestDialogJob.id);
      fetchApplications();
      fetchCommandCenterData();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to shortlist'));
    } finally {
      setBestSubmitting(false);
    }
  };

  // ---------- Wave 2: Add note (uses changeStage to same stage to record remark) ----------
  const submitNote = async () => {
    if (!selectedApp || !newNote.trim()) return;
    setSavingNote(true);
    try {
      await applicationsApi.changeStage(selectedApp.id, {
        stage: selectedApp.stage,
        remarks: newNote.trim(),
        client_feedback: null,
      });
      toast.success('Note added');
      setNewNote('');
      await fetchApplications();
      fetchCommandCenterData();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to add note'));
    } finally {
      setSavingNote(false);
    }
  };

  // ---------- Filtering ----------
  const filteredApplications = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return applications.filter((a) => {
      if (stageFilter !== 'all' && a.stage !== stageFilter) return false;
      if (!q) return true;
      const hay = `${a.candidate_name || ''} ${a.job_title || ''} ${a.client_name || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [applications, searchTerm, stageFilter]);

  const stageBuckets = useMemo(() => {
    const buckets = STAGES.reduce((acc, s) => ({ ...acc, [s.id]: [] }), {});
    filteredApplications.forEach((a) => {
      if (buckets[a.stage]) buckets[a.stage].push(a);
    });
    return buckets;
  }, [filteredApplications]);

  // ---------- Search jump & cycle ----------
  // Reset cycle position whenever the search term / scope changes
  useEffect(() => {
    setMatchIndex(null);
  }, [searchTerm, stageFilter, selectedJobId]);

  // Scroll-to + flash-highlight the given application card
  const jumpToApp = useCallback(
    (app) => {
      if (!app) return;
      setMobileTab('pipeline');
      const stageId = app.stage;
      const bucket = stageBuckets[stageId] || [];
      const indexInBucket = bucket.findIndex((a) => a.id === app.id);

      const listApi = listRefs.current[stageId];
      if (listApi && indexInBucket >= 0) {
        try {
          listApi.scrollToRow({
            index: indexInBucket,
            align: 'center',
            behavior: 'smooth',
          });
        } catch (_) {
          /* index out of range — ignore */
        }
      }

      // DOM scrollIntoView: handles inline columns + horizontal nudge
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="cc-app-card-${app.id}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
      }, 50);

      setHighlightedAppId(app.id);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => {
        setHighlightedAppId(null);
      }, HIGHLIGHT_DURATION_MS);
    },
    [stageBuckets]
  );

  // Cycle through visible matches (Enter / ↓ → next, ↑ → prev)
  const cycleMatch = useCallback(
    (direction /* 'next' | 'prev' */) => {
      if (!searchTerm.trim()) return;
      const m = filteredApplications;
      if (m.length === 0) {
        toast.info('No candidate found for this search.');
        return;
      }
      let next;
      if (direction === 'next') {
        next = matchIndex === null ? 0 : (matchIndex + 1) % m.length;
      } else {
        next =
          matchIndex === null
            ? m.length - 1
            : (matchIndex - 1 + m.length) % m.length;
      }
      setMatchIndex(next);
      jumpToApp(m[next]);
    },
    [filteredApplications, matchIndex, searchTerm, jumpToApp]
  );

  // ---------- Drag & Drop handlers ----------
  const handleDragStart = (e, appId) => {
    setDragAppId(appId);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', appId);
    } catch (_) {
      // some browsers throw when readonly
    }
  };

  const handleDragEnd = () => {
    setDragAppId(null);
    setDragOverStage(null);
  };

  const handleDragOverColumn = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stageId) setDragOverStage(stageId);
  };

  const handleDragLeaveColumn = (stageId) => {
    if (dragOverStage === stageId) setDragOverStage(null);
  };

  const handleDropOnStage = (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    const appId = dragAppId || e.dataTransfer.getData('text/plain');
    setDragAppId(null);
    if (!appId) return;
    const app = applications.find((a) => a.id === appId);
    if (!app) return;
    if (app.stage === targetStage) return; // no-op
    setPendingStageChange({ app, targetStage });
    setStageRemark('');
    setClientFeedback('');
    // Default the Offered CTC field to candidate's expected_ctc → current_ctc
    // so recruiters don't have to retype if the value is already known.
    // Pre-fill Offered CTC in LPA. Candidate profile values are stored as
    // rupees so we divide by 100000 for the display. The submit handler
    // multiplies back, keeping the DB in rupees (single source of truth).
    const rawCtc = app.expected_ctc || app.candidate_expected_ctc
      || app.current_ctc || app.candidate_current_ctc || '';
    const prefillLakhs = rawCtc
      ? String(Math.round((Number(rawCtc) / 100000) * 100) / 100)
      : '';
    setOfferedCtc(targetStage === 'joined' ? prefillLakhs : '');
    // Pre-fill joining date with today; recruiter can change to actual joining date.
    setJoiningDate(targetStage === 'joined' ? new Date().toISOString().slice(0, 10) : '');
    // Reset drop reason every time. Mandatory only for `dropped_out`.
    setDroppedReason('');
    setDroppedReasonText('');
  };

  const closeStageDialog = () => {
    if (savingStage) return;
    setPendingStageChange(null);
    setStageRemark('');
    setClientFeedback('');
    setOfferedCtc('');
    setJoiningDate('');
    setDroppedReason('');
    setDroppedReasonText('');
  };

  const confirmStageChange = async () => {
    if (!pendingStageChange) return;
    const { app, targetStage } = pendingStageChange;
    const stageMeta = STAGE_BY_ID[targetStage];
    // Require remark for destructive lanes
    if (stageMeta?.destructive && !stageRemark.trim()) {
      toast.error('Please add a remark explaining why.');
      return;
    }
    setSavingStage(true);
    try {
      // For JOINED transitions, validate + send the offered CTC so we can
      // compute Candidate CTC totals correctly. Empty input is allowed
      // (backwards-compat with old apps); we just don't persist a value.
      let offeredCtcNum = null;
      let joiningDateValue = null;
      if (targetStage === 'joined') {
        // Joining date is mandatory client-side too — show a friendly toast
        // before hitting the backend (which also enforces it as a 400).
        if (!joiningDate) {
          toast.error('Joining date is mandatory when marking as Joined.');
          setSavingStage(false);
          return;
        }
        joiningDateValue = joiningDate;
        if (offeredCtc !== '') {
          const n = Number(offeredCtc);
          if (Number.isNaN(n) || n < 0) {
            toast.error('Offered CTC must be a positive number.');
            setSavingStage(false);
            return;
          }
          // User types CTC in LPA (e.g. 12.5 = ₹12.5L). Persist in rupees
          // so all downstream aggregations share a single unit.
          offeredCtcNum = Math.round(n * 100000);
        }
      }
      // Validate dropped-out: reason is mandatory; if "other", text is mandatory.
      if (targetStage === 'dropped_out') {
        if (!droppedReason) {
          toast.error('Please select a reason for dropping the candidate.');
          setSavingStage(false);
          return;
        }
        if (droppedReason === 'other' && !droppedReasonText.trim()) {
          toast.error('Please describe the reason.');
          setSavingStage(false);
          return;
        }
      }
      await applicationsApi.changeStage(app.id, {
        stage: targetStage,
        remarks: stageRemark.trim() || null,
        client_feedback: clientFeedback.trim() || null,
        offered_ctc: offeredCtcNum,
        joining_date: joiningDateValue,
        dropped_reason: targetStage === 'dropped_out' ? droppedReason : null,
        dropped_reason_text: targetStage === 'dropped_out' && droppedReason === 'other' ? droppedReasonText.trim() : null,
      });
      toast.success(`Moved to ${stageMeta?.label || targetStage}`);
      // Optimistic local update so UI feels instant; then refresh
      setApplications((prev) =>
        prev.map((a) => (a.id === app.id ? { ...a, stage: targetStage } : a))
      );
      setPendingStageChange(null);
      setStageRemark('');
      setClientFeedback('');
      setOfferedCtc('');
      setJoiningDate('');
      fetchApplications();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to update stage'));
    } finally {
      setSavingStage(false);
    }
  };

  // ---------- Quick actions on right panel ----------
  const quickRejectFromPanel = () => {
    if (!selectedApp) return;
    setPendingStageChange({ app: selectedApp, targetStage: 'rejected' });
    setStageRemark('');
    setClientFeedback('');
    setOfferedCtc('');
    setJoiningDate('');
  };

  const quickHoldFromPanel = () => {
    if (!selectedApp) return;
    setPendingStageChange({ app: selectedApp, targetStage: 'dropped' });
    setStageRemark('');
    setClientFeedback('');
    setOfferedCtc('');
  };

  // "Dropped" = candidate withdrew permanently. Distinct from "On Hold"
  // (paused, may resume). Surfaced as its own action so recruiters always
  // make the intent explicit.
  const quickDroppedOutFromPanel = () => {
    if (!selectedApp) return;
    setPendingStageChange({ app: selectedApp, targetStage: 'dropped_out' });
    setStageRemark('');
    setClientFeedback('');
    setOfferedCtc('');
  };

  // Job priority heuristic (count of open apps per job, fake "hot" tag if > 5)
  const appCountByJob = useMemo(() => {
    const map = {};
    applications.forEach((a) => {
      if (!a.job_id) return;
      map[a.job_id] = (map[a.job_id] || 0) + 1;
    });
    return map;
  }, [applications]);

  const visibleJobs = useMemo(() => {
    return jobs.filter(
      (j) => j.status === 'published' || j.status === 'assigned' || j.status === 'approved'
    );
  }, [jobs]);

  // ---------- Wave 2: Per-candidate filtered activity & tasks ----------
  const candidateActivity = useMemo(() => {
    if (!selectedApp) return [];
    return recentActivity.filter(
      (it) =>
        it.application_id === selectedApp.id ||
        it.candidate_id === selectedApp.candidate_id
    );
  }, [recentActivity, selectedApp]);

  const candidateTasks = useMemo(() => {
    if (!selectedApp) return [];
    return dailyTasks.filter(
      (t) =>
        t.application_id === selectedApp.id ||
        t.candidate_id === selectedApp.candidate_id
    );
  }, [dailyTasks, selectedApp]);

  // Reset note composer when candidate changes (bottom-tab is persisted per user)
  useEffect(() => {
    setNewNote('');
  }, [selectedApp?.id]);

  // ---------- Drill-down: open a metric tile to inspect underlying records ----------
  const openDrillDown = useCallback(async (metricKey) => {
    if (!DRILLABLE_METRICS.includes(metricKey)) return;
    setDrillMetric(metricKey);
    setDrillItems([]);
    setDrillSummary({});
    setDrillLoading(true);
    try {
      // Pass the active custom range so the drill-down list matches the tile
      // count. Without this the dialog defaults to today/month and the user
      // sees "Closures 35" from May while the April tile shows 6.
      const range =
        ccRangeActive && ccStart && ccEnd && ccStart <= ccEnd
          ? { start_date: ccStart, end_date: ccEnd }
          : null;
      const res = await commandCenterApi.getMetricItems(metricKey, 100, range);
      setDrillItems(res.data?.items || []);
      setDrillSummary(res.data?.summary || {});
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load details'));
      setDrillMetric(null);
    } finally {
      setDrillLoading(false);
    }
  }, [ccRangeActive, ccStart, ccEnd]);

  // Click a drill-down row → jump to that candidate in the right panel
  const handleDrillRowClick = useCallback((item) => {
    if (!item) return;
    if (item.job_id) setSelectedJobId(item.job_id);
    else setSelectedJobId('all');
    if (item.application_id) {
      setSelectedAppId(item.application_id);
      setMobileTab('candidate');
    }
    setDrillMetric(null);
  }, []);

  // Track which drill row is currently being saved (one-click Mark as Joined)
  const [drillSavingId, setDrillSavingId] = useState(null);
  // Pending Joining drill: which row is asking for drop confirmation. Inline
  // (no modal) keeps the action fast but the two-step click prevents accidental
  // drops from a stray click.
  const [drillDropConfirmId, setDrillDropConfirmId] = useState(null);

  // Pending Joining drill-down: mark an offer as JOINED in one click without
  // routing the user to the candidate panel. Closes the loop on ghosted offers
  // for which paperwork + joining date already exist.
  // Note: backend requires joining_date in body even if app doc has one — we
  // pass the row's `date` (which is the existing joining_date for this metric).
  const markAsJoinedFromDrill = useCallback(async (item, e) => {
    if (e) e.stopPropagation();
    if (!item?.application_id || drillSavingId) return;
    setDrillSavingId(item.application_id);
    try {
      await applicationsApi.changeStage(item.application_id, {
        stage: 'joined',
        joining_date: item.date,  // YYYY-MM-DD from the pending_joining drill row
        remarks: 'Marked as joined from Pending Joining drill-down',
        client_feedback: null,
      });
      toast.success(`${item.candidate_name || 'Candidate'} marked as Joined`);
      // Optimistically drop the row from the dialog so the count updates instantly
      setDrillItems((prev) => prev.filter((x) => x.application_id !== item.application_id));
      // Refresh KPI tiles + parent application list
      fetchCommandCenterData();
      fetchApplications();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to mark as joined'));
    } finally {
      setDrillSavingId(null);
    }
  }, [drillSavingId, fetchCommandCenterData, fetchApplications]);

  // Pending Joining drill-down: mark a ghosted offer as DROPPED_OUT. Pre-fills
  // dropped_reason=lost_contact (the typical "candidate vanished" pattern).
  // Requires a second click on "Confirm drop" so we never destroy a row from a
  // misclick. Stays inside the drill dialog.
  const markAsDroppedFromDrill = useCallback(async (item, e) => {
    if (e) e.stopPropagation();
    if (!item?.application_id || drillSavingId) return;
    setDrillSavingId(item.application_id);
    try {
      await applicationsApi.changeStage(item.application_id, {
        stage: 'dropped_out',
        dropped_reason: 'lost_contact',
        remarks: 'Marked as dropped (ghosted offer) from Pending Joining drill-down',
        client_feedback: null,
      });
      toast.success(`${item.candidate_name || 'Candidate'} marked as Dropped`);
      setDrillItems((prev) => prev.filter((x) => x.application_id !== item.application_id));
      fetchCommandCenterData();
      fetchApplications();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to mark as dropped'));
    } finally {
      setDrillSavingId(null);
      setDrillDropConfirmId(null);
    }
  }, [drillSavingId, fetchCommandCenterData, fetchApplications]);

  // ---------- Stage tabs: rich list view + bulk actions ----------
  const fetchStageList = useCallback(async (stageId, jobId) => {
    if (stageId === 'all') return;
    setStageListLoading(true);
    setBulkSelected(new Set());
    try {
      const res = await commandCenterApi.getStageCandidates(stageId, jobId, 200);
      setStageList(res.data?.items || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load stage candidates'));
      setStageList([]);
    } finally {
      setStageListLoading(false);
    }
  }, []);

  // Auto-fetch when the active tab or job filter changes
  useEffect(() => {
    if (activeStageTab !== 'all') {
      fetchStageList(activeStageTab, selectedJobId);
    } else {
      setStageList([]);
      setBulkSelected(new Set());
    }
  }, [activeStageTab, selectedJobId, fetchStageList]);

  const toggleBulkRow = useCallback((appId) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(appId)) next.delete(appId);
      else next.add(appId);
      return next;
    });
  }, []);

  const toggleBulkAll = useCallback(() => {
    setBulkSelected((prev) => {
      if (prev.size === stageList.length && stageList.length > 0) return new Set();
      return new Set(stageList.map((it) => it.application_id));
    });
  }, [stageList]);

  const clearBulk = useCallback(() => setBulkSelected(new Set()), []);

  const submitBulkAction = useCallback(async () => {
    if (!bulkAction || bulkSelected.size === 0) return;
    if (bulkAction.mode === 'reject' && !bulkRemarks.trim()) {
      toast.error('A reason is required when bulk-rejecting candidates.');
      return;
    }
    setBulkSubmitting(true);
    try {
      const res = await applicationsApi.bulkChangeStage({
        application_ids: Array.from(bulkSelected),
        target_stage: bulkAction.target_stage,
        remarks: bulkRemarks.trim() || null,
      });
      const moved = res.data?.moved ?? 0;
      const missing = res.data?.missing ?? [];
      if (moved > 0) {
        const stageLabel = STAGE_BY_ID[bulkAction.target_stage]?.label || bulkAction.target_stage;
        toast.success(
          `Moved ${moved} candidate${moved === 1 ? '' : 's'} to ${stageLabel}` +
            (missing.length ? ` · ${missing.length} skipped` : '')
        );
      } else {
        toast.error('No candidates were moved. Try refreshing the page.');
      }
      // Refresh the parent applications list so the kanban + drill data stays in sync
      await fetchApplications();
      // Re-fetch the current stage list so moved rows disappear
      await fetchStageList(activeStageTab, selectedJobId);
      setBulkAction(null);
      setBulkRemarks('');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Bulk action failed'));
    } finally {
      setBulkSubmitting(false);
    }
  }, [bulkAction, bulkSelected, bulkRemarks, activeStageTab, selectedJobId, fetchStageList, fetchApplications]);

  // ---------- Wave 2: KPI tile config ----------
  const metricTiles = useMemo(() => {
    const m = metrics || {};
    // When a custom range is active, swap "today" / "this month" labels for
    // a single range-aware tag so users don't read the wrong value.
    // Otherwise drop the redundant time qualifier — the date-range tabs above
    // already make the window obvious and these labels truncate at 1440px.
    const rangeOn = !!m.range_active;
    const rangeTag = rangeOn ? 'in range' : null;
    const subLabel = rangeOn ? `Submissions ${rangeTag}` : 'Submissions';
    const ivLabel = rangeOn ? `Interviews ${rangeTag}` : 'Interviews';
    const closeLabel = rangeOn ? `Closures ${rangeTag}` : 'Closures';
    const convLabel = rangeOn ? `Conversion ${rangeTag}` : 'Conversion';
    const tiles = [
      {
        key: 'submissions',
        label: subLabel,
        value: m.submissions_today ?? '—',
        icon: Send,
        color: 'text-sky-600 bg-sky-50 border-sky-100',
        description: rangeOn
          ? 'Applications created (any stage) within the selected custom date range. Source: applications.created_at.'
          : 'Applications created today (00:00 → now, in your timezone). Counts a candidate the first time they enter the pipeline; later stage moves do not re-count.',
      },
      {
        key: 'interviews',
        label: ivLabel,
        value: m.interviews_today ?? '—',
        icon: Calendar,
        color: 'text-amber-600 bg-amber-50 border-amber-100',
        description: rangeOn
          ? 'Applications whose stage_history shows an Interview Scheduled / Interview Done event within the selected range.'
          : 'Interviews scheduled or completed today, picked from each application\'s stage_history.',
      },
      {
        key: 'closures',
        label: closeLabel,
        value: m.closures_this_month ?? '—',
        icon: Award,
        color: 'text-emerald-600 bg-emerald-50 border-emerald-100',
        description: rangeOn
          ? 'Applications whose JOINED stage event falls inside the selected range. Uses the JOINED transition date, not last-updated time.'
          : 'Joins recorded this calendar month (1st → today). Each joined candidate counts once, even if their record was edited multiple times.',
      },
      {
        key: 'conversion',
        label: convLabel,
        value: m.conversion_pct != null ? `${m.conversion_pct}%` : '—',
        icon: TrendingUp,
        color: 'text-violet-600 bg-violet-50 border-violet-100',
        description: 'Joined ÷ Submissions × 100 for the current scope window. Can exceed 100% when joins in the window come from older submissions — that\'s mathematically correct, not a bug.',
      },
      {
        key: 'tat',
        label: 'Avg TAT',
        value: m.tat_days != null ? `${m.tat_days}` : '—',
        icon: Clock,
        color: 'text-teal-600 bg-teal-50 border-teal-100',
        description: 'Average Time to Achieve Target — calendar days between application created_at and JOINED stage event, averaged over candidates joined this month. Lower is better.',
      },
      {
        key: 'pending',
        label: 'Pending tasks',
        value: m.pending_tasks_count ?? '—',
        icon: ListTodo,
        color: 'text-orange-600 bg-orange-50 border-orange-100',
        description: 'Auto-generated to-do list: stale screenings (>3d in screening), interviews today, offers pending feedback, and offer_released apps without a joining decision. Click the tile to see the full breakdown.',
      },
    ];
    if (m.pending_paperwork_count != null && m.pending_paperwork_count > 0) {
      const stale = m.pending_paperwork_stale_count || 0;
      tiles.push({
        key: 'pending_paperwork',
        label: 'Pending paperwork',
        // Stale count moves to a small badge next to the value so the label
        // stays short and survives narrow tiles.
        badge: stale > 0 ? `${stale} stale` : null,
        value: m.pending_paperwork_count,
        icon: FileText,
        // Stale items get the rose treatment to nudge action; otherwise yellow.
        color: stale > 0
          ? 'text-rose-600 bg-rose-50 border-rose-100'
          : 'text-yellow-700 bg-yellow-50 border-yellow-100',
        description: 'Applications moved to Offer Released but without an offer_letter_date on file. Stale = waiting ≥ 3 days. Click to see the longest-waiting candidates first.',
      });
    }
    if (m.pending_joining_count != null && m.pending_joining_count > 0) {
      const stale = m.pending_joining_stale_count || 0;
      tiles.push({
        key: 'pending_joining',
        label: 'Pending joining',
        badge: stale > 0 ? `${stale} overdue` : null,
        value: m.pending_joining_count,
        icon: CalendarClock,
        // Overdue joins are always urgent — fuchsia/rose for >=7d, amber otherwise.
        color: stale > 0
          ? 'text-fuchsia-600 bg-fuchsia-50 border-fuchsia-100'
          : 'text-amber-700 bg-amber-50 border-amber-100',
        description: 'Offers with paperwork done + joining_date in the past but never marked as Joined ("ghosted offers"). Stale = joining_date ≥ 7 days past. One-click Mark Joined / Mark Dropped available in the drill-down.',
      });
    }
    if (m.joined_ctc_total != null && showRevenue) {
      tiles.push({
        key: 'joined_ctc_total',
        label: 'Joined CTC',
        value: formatINR(m.joined_ctc_total) || '—',
        icon: IndianRupee,
        color: 'text-rose-600 bg-rose-50 border-rose-100',
        description: 'Sum of offered_ctc for applications whose JOINED stage event falls inside the current window. Date used is the JOINED transition, not the document\'s last-updated time. Values above ₹50 Cr per row are excluded as data sanity guards.',
      });
    }
    return tiles;
  }, [metrics, isManager, showRevenue]);

  // ---------- Wave 3: tile customization (pin/hide per recruiter) ----------
  // Each user can choose which tiles to "pin" in the strip; the rest tuck
  // into a "+N more" popover at the end. Defaults to ALL tiles visible so the
  // current behaviour is preserved for anyone who never opens the menu.
  const TILE_PREF_KEY = 'cc-tile-prefs-v1';
  const [hiddenTileKeys, setHiddenTileKeys] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(TILE_PREF_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const persistHidden = useCallback((next) => {
    setHiddenTileKeys(next);
    try { localStorage.setItem(TILE_PREF_KEY, JSON.stringify(next)); } catch { /* quota */ }
  }, []);
  const toggleTileHidden = useCallback((key) => {
    const next = hiddenTileKeys.includes(key)
      ? hiddenTileKeys.filter((k) => k !== key)
      : [...hiddenTileKeys, key];
    persistHidden(next);
  }, [hiddenTileKeys, persistHidden]);
  const resetTilePrefs = useCallback(() => persistHidden([]), [persistHidden]);
  // Controlled state for the "+N more" popover so we can dismiss it when the
  // user clicks a drillable hidden tile and the drill-down dialog opens.
  const [customizeOpen, setCustomizeOpen] = useState(false);

  // Split tiles into pinned (rendered in the strip) and hidden (in "+N more")
  const { pinnedTiles, hiddenTiles } = useMemo(() => {
    const pinned = [];
    const hidden = [];
    for (const t of metricTiles) {
      if (hiddenTileKeys.includes(t.key)) hidden.push(t);
      else pinned.push(t);
    }
    return { pinnedTiles: pinned, hiddenTiles: hidden };
  }, [metricTiles, hiddenTileKeys]);

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)]" data-testid="command-center-page">
      {/* ==================== TOP BAR ==================== */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1
              className="text-2xl sm:text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Recruiter Command Center
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Single-screen view of jobs, pipeline and candidates.
            </p>
          </div>
          <Badge variant="outline" className="text-xs" data-testid="cc-pipeline-count">
            {filteredApplications.length} of {applications.length} in view
          </Badge>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  cycleMatch('next');
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  cycleMatch('prev');
                } else if (e.key === 'Escape') {
                  setSearchTerm('');
                }
              }}
              placeholder="Search candidate, job or client... (Enter / ↓ ↑ to cycle)"
              className={`pl-9 ${searchTerm ? 'pr-[110px]' : 'pr-9'}`}
              data-testid="cc-search-input"
            />

            {/* Counter + cycle controls (visible only when searching) */}
            {searchTerm && (
              <div
                className="absolute right-9 top-1/2 -translate-y-1/2 flex items-center gap-1"
                data-testid="cc-search-counter-group"
              >
                <span
                  className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                    filteredApplications.length === 0
                      ? 'bg-red-50 text-red-600 border border-red-200'
                      : 'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}
                  data-testid="cc-search-counter"
                >
                  {filteredApplications.length === 0
                    ? '0/0'
                    : `${matchIndex === null ? '–' : matchIndex + 1}/${filteredApplications.length}`}
                </span>
                <button
                  type="button"
                  onClick={() => cycleMatch('prev')}
                  disabled={filteredApplications.length === 0}
                  className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30"
                  aria-label="Previous match"
                  data-testid="cc-search-prev"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => cycleMatch('next')}
                  disabled={filteredApplications.length === 0}
                  className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30"
                  aria-label="Next match"
                  data-testid="cc-search-next"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="cc-search-clear"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-[140px] sm:w-[160px]" data-testid="cc-stage-filter">
              <SelectValue placeholder="All stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {STAGES.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <JobsCombobox
            jobs={visibleJobs}
            value={selectedJobId}
            onChange={handleSelectJob}
            appCountByJob={appCountByJob}
            onBest10={openBestCandidatesDialog}
            loading={jobsLoading}
          />

          {/* Owner lens — manager-only. Sits next to the filters so the
              user understands the toggle re-scopes the same data shown
              below, NOT a separate view. Mobile pushes it onto a second
              row via the parent flex-wrap. */}
          <OwnerLensToggle
            value={ownerLens}
            onChange={setOwnerLens}
            visible={isManager}
            testid="cc-owner-lens"
          />
        </div>

        {/* Proactive: warn about broken resume references in this pipeline */}
        <BrokenResumesBanner user={user} />

        {/* Recruiter monthly closure goal nudge */}
        <GoalBanner />

        {/* NOTE: TodaysWinBanner + the KPI metrics strip (Submissions /
            Interviews / Closures / Conversion / TAT / Pending / Joined CTC)
            + the date-range filter + the customize-tiles popover were
            relocated to the Dashboard (`<CommandCenterSummary>`) on
            2026-06-04. The drill-down dialog at the bottom of this file
            is no longer reachable from this page — it only fires from the
            Dashboard tile click. Left in-place for now to avoid invasive
            state removal mid-feature; will be pruned in a follow-up
            refactor along with the metric* state hooks. */}

        {/* Offer → Joining compact strip — respects the same date filter as the KPI tiles */}
        <div className="mb-3">
          <OfferVsJoiningCard
            variant="compact"
            period={ccRangeActive && ccStart && ccEnd && ccStart <= ccEnd ? 'custom' : 'month'}
            startDate={ccRangeActive ? ccStart : undefined}
            endDate={ccRangeActive ? ccEnd : undefined}
            testid="cc-ovj"
          />
        </div>
      </div>


      {/* Mobile/tablet segmented tab switcher (hidden on lg+ where 2-pane fits) */}
      <div
        className="lg:hidden mb-3 grid grid-cols-2 rounded-lg border border-slate-200 bg-slate-50 p-1 gap-1"
        data-testid="cc-mobile-tabs"
        role="tablist"
        aria-label="Command Center sections"
      >
        {[
          { key: 'pipeline', label: 'Pipeline', icon: Target, count: filteredApplications.length },
          { key: 'candidate', label: 'Candidate', icon: User, count: selectedApp ? 1 : 0 },
        ].map((t) => {
          const Icon = t.icon;
          const active = mobileTab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setMobileTab(t.key)}
              data-testid={`cc-mobile-tab-${t.key}`}
              className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-medium transition-colors ${
                active
                  ? 'bg-white text-teal-700 shadow-sm border border-teal-200'
                  : 'text-slate-600 hover:bg-white/60'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{t.label}</span>
              {t.count > 0 && (
                <span
                  className={`text-[10px] font-mono px-1 rounded ${
                    active ? 'bg-teal-50 text-teal-700' : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ==================== 2-PANE GRID (jobs panel replaced by searchable dropdown above) ==================== */}
      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        {/* Center: Kanban or stage-list */}
        <section
          className={`${mobileTab === 'pipeline' ? 'block' : 'hidden'} lg:block col-span-12 lg:col-span-9 min-h-0`}
          data-testid="cc-pipeline-board"
        >
          <Card className="h-full flex flex-col">
            <div className="px-4 pt-3 pb-2 border-b">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-semibold text-slate-800 text-sm">
                  {activeStageTab === 'all' ? 'Live Pipeline' : `${STAGE_BY_ID[activeStageTab]?.label || activeStageTab} candidates`}
                </h2>
                {(appsLoading || stageListLoading) && (
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                )}
              </div>
              {/* Stage tab strip */}
              <div
                className="flex items-center gap-1 overflow-x-auto pb-1 -mx-1 px-1"
                data-testid="cc-stage-tabs"
                role="tablist"
                aria-label="Pipeline stages"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeStageTab === 'all'}
                  onClick={() => setActiveStageTab('all')}
                  data-testid="cc-stage-tab-all"
                  className={`flex-shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold transition border ${
                    activeStageTab === 'all'
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200'
                  }`}
                >
                  All stages
                  <span className={`ml-1.5 px-1 rounded text-[10px] font-mono ${
                    activeStageTab === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {filteredApplications.length}
                  </span>
                </button>
                {STAGES.map((s) => {
                  // Use the live stage-candidates total for the currently
                  // active tab so the badge stays in sync with the rich
                  // detail list below. The Kanban-derived `stageBuckets`
                  // count is capped at the page_size=200 Kanban fetch and
                  // diverges from reality on busy orgs (caused the prod
                  // "Interview 4" bug where the list showed 7+ rows).
                  const isActive = activeStageTab === s.id;
                  const liveCount =
                    isActive && !stageListLoading ? stageList.length : null;
                  const count =
                    liveCount != null ? liveCount : (stageBuckets[s.id] || []).length;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveStageTab(s.id)}
                      data-testid={`cc-stage-tab-${s.id}`}
                      className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition border ${
                        isActive
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                      <span>{s.label}</span>
                      <span className={`ml-0.5 px-1 rounded text-[10px] font-mono ${
                        isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {activeStageTab === 'all' ? (
              <div className="flex-1 min-h-0 overflow-x-auto">
                <div className="flex gap-3 p-3 h-full min-w-max">
                  {STAGES.map((stage) => {
                    const items = stageBuckets[stage.id] || [];
                    const isOver = dragOverStage === stage.id;
                    return (
                      <div
                        key={stage.id}
                        data-testid={`cc-stage-${stage.id}`}
                        className={`flex flex-col w-[230px] flex-shrink-0 rounded-lg border-2 transition-colors ${
                          isOver
                            ? 'border-teal-400 bg-teal-50'
                            : `${stage.col}`
                        }`}
                        onDragOver={(e) => handleDragOverColumn(e, stage.id)}
                        onDragLeave={() => handleDragLeaveColumn(stage.id)}
                        onDrop={(e) => handleDropOnStage(e, stage.id)}
                      >
                        <div className="px-3 py-2 flex items-center gap-2 border-b border-slate-200/60 bg-white/60 backdrop-blur-sm">
                          <span className={`w-2.5 h-2.5 rounded-full ${stage.dot}`} />
                          <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                            {stage.label}
                          </span>
                          <span
                            className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full ${
                              items.length > 0
                                ? 'bg-slate-900 text-white'
                                : 'bg-slate-200 text-slate-500'
                            }`}
                            data-testid={`cc-stage-count-${stage.id}`}
                          >
                            {items.length}
                          </span>
                        </div>
                        <div
                          className="flex-1 min-h-0"
                          data-testid={`cc-stage-body-${stage.id}`}
                        >
                          {items.length === 0 ? (
                            <div className="text-[11px] text-slate-400 text-center py-6">
                              {isOver ? 'Drop here' : 'Empty'}
                            </div>
                          ) : items.length > VIRTUALIZE_THRESHOLD ? (
                            <div
                              className="h-full"
                              data-testid={`cc-stage-virtualized-${stage.id}`}
                            >
                              <VirtualList
                                listRef={(api) => {
                                  if (api) listRefs.current[stage.id] = api;
                                  else delete listRefs.current[stage.id];
                                }}
                                rowCount={items.length}
                                rowHeight={ROW_HEIGHT}
                                rowComponent={VirtualKanbanRow}
                                rowProps={{
                                  items,
                                  selectedAppId,
                                  dragAppId,
                                  highlightedAppId,
                                  onSelect: handleSelectApp,
                                  onDragStart: handleDragStart,
                                  onDragEnd: handleDragEnd,
                                }}
                                defaultHeight={500}
                                overscanCount={4}
                                style={{ height: '100%' }}
                              />
                            </div>
                          ) : (
                            <ScrollArea className="h-full">
                              <div className="p-2 space-y-2">
                                {items.map((app) => (
                                  <KanbanCard
                                    key={app.id}
                                    app={app}
                                    isSelected={selectedAppId === app.id}
                                    isDragging={dragAppId === app.id}
                                    isHighlighted={highlightedAppId === app.id}
                                    onSelect={handleSelectApp}
                                    onDragStart={handleDragStart}
                                    onDragEnd={handleDragEnd}
                                  />
                                ))}
                              </div>
                            </ScrollArea>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <StageDetailList
                stage={STAGE_BY_ID[activeStageTab]}
                items={stageList}
                loading={stageListLoading}
                selectedAppId={selectedAppId}
                onSelect={handleSelectApp}
                bulkSelected={bulkSelected}
                onToggleRow={toggleBulkRow}
                onToggleAll={toggleBulkAll}
                onClearBulk={clearBulk}
                onBulkAction={(mode, target_stage) => {
                  setBulkAction({ mode, target_stage });
                  setBulkRemarks('');
                }}
              />
            )}
          </Card>
        </section>

        {/* Right: Candidate Detail */}
        <aside
          className={`${mobileTab === 'candidate' ? 'block' : 'hidden'} lg:block col-span-12 lg:col-span-3 min-h-0`}
          data-testid="cc-candidate-panel"
        >
          <Card className="h-full flex flex-col">
            {!selectedApp ? (
              <div className="flex-1 flex items-center justify-center text-center text-slate-400 p-6 text-sm">
                <div>
                  <User className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  Select a candidate card<br />to see details here.
                </div>
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <h2 className="font-semibold text-slate-800 text-sm">Candidate Detail</h2>
                  <button
                    onClick={() => {
                      setSelectedAppId(null);
                      setMobileTab('pipeline');
                    }}
                    className="text-slate-400 hover:text-slate-600"
                    data-testid="cc-close-detail"
                    aria-label="Close detail"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <ScrollArea className="flex-1">
                  <div className="p-4 space-y-4">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-teal-700" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p
                          className="font-semibold text-base text-slate-900 truncate leading-tight"
                          data-testid="cc-detail-name"
                          title={selectedApp.candidate_name || 'Unknown'}
                        >
                          {selectedApp.candidate_name || 'Unknown'}
                        </p>
                        <p
                          className="text-xs text-slate-500 truncate mt-0.5"
                          title={selectedApp.job_title || ''}
                        >
                          {selectedApp.job_title || '—'}
                        </p>
                        <Badge
                          className="mt-1.5 text-[10px] font-mono"
                          variant="secondary"
                        >
                          {STAGE_BY_ID[selectedApp.stage]?.label || selectedApp.stage}
                        </Badge>
                      </div>
                    </div>

                    {/* Candidate quick fields — click to edit (RBAC-enforced server-side) */}
                    {candidateLoading ? (
                      <div className="flex items-center justify-center py-4 text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    ) : candidateDetail ? (
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <EditableDetailField
                          label="Experience"
                          field="experience_years"
                          rawValue={candidateDetail.experience_years}
                          type="number"
                          suffix="yrs"
                          format={(v) => (v != null && v !== '' ? `${v} yrs` : null)}
                          onSave={saveCandidateField}
                        />
                        <EditableDetailField
                          label="Location"
                          field="current_location"
                          rawValue={candidateDetail.current_location}
                          type="text"
                          onSave={saveCandidateField}
                        />
                        {showRevenue && (
                          <EditableDetailField
                            label="Current CTC"
                            field="current_ctc"
                            rawValue={candidateDetail.current_ctc}
                            type="number"
                            suffix="L"
                            format={(v) => formatINR(v)}
                            onSave={saveCandidateField}
                          />
                        )}
                        {showRevenue && (
                          <EditableDetailField
                            label="Expected CTC"
                            field="expected_ctc"
                            rawValue={candidateDetail.expected_ctc}
                            type="number"
                            suffix="L"
                            format={(v) => formatINR(v)}
                            onSave={saveCandidateField}
                          />
                        )}
                        {showRevenue && selectedApp && ['offer_released', 'interview_selected', 'joined', 'dropped'].includes(selectedApp.stage) ? (
                          <EditableDetailField
                            label="Offered CTC"
                            field="offered_ctc"
                            // Edit + display in LPA. Persisted as rupees by saveOfferedCtc.
                            rawValue={selectedApp.offered_ctc != null
                              ? Math.round((Number(selectedApp.offered_ctc) / 100000) * 100) / 100
                              : null}
                            type="number"
                            suffix="L"
                            format={(v) => (v != null && v !== '' ? `₹${Number(v).toLocaleString('en-IN')} L` : null)}
                            onSave={saveOfferedCtc}
                          />
                        ) : null}
                        {selectedApp && ['offer_released', 'interview_selected', 'joined'].includes(selectedApp.stage) ? (
                          <EditableDetailField
                            label="Offer Letter Date"
                            field="offer_letter_date"
                            rawValue={selectedApp.offer_letter_date || null}
                            type="date"
                            format={(v) => {
                              if (!v) return null;
                              try {
                                const d = new Date(`${v}T00:00:00`);
                                if (isNaN(d.getTime())) return v;
                                return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                              } catch (_) { return v; }
                            }}
                            onSave={saveOfferLetterDate}
                            fallback="Not set"
                          />
                        ) : null}
                        {selectedApp && ['offer_released', 'interview_selected', 'joined'].includes(selectedApp.stage) ? (
                          <EditableDetailField
                            label="Joining Date"
                            field="joining_date"
                            rawValue={selectedApp.joining_date || null}
                            type="date"
                            format={(v) => {
                              if (!v) return null;
                              try {
                                const d = new Date(`${v}T00:00:00`);
                                if (isNaN(d.getTime())) return v;
                                return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                              } catch (_) { return v; }
                            }}
                            onSave={saveJoiningDate}
                            fallback="Not set"
                          />
                        ) : null}
                        <EditableDetailField
                          label="Notice"
                          field="notice_period"
                          rawValue={candidateDetail.notice_period}
                          type="number"
                          suffix="d"
                          format={(v) => (v != null && v !== '' ? `${v} d` : null)}
                          onSave={saveCandidateField}
                        />
                        <EditableDetailField
                          label="Email"
                          field="email"
                          rawValue={candidateDetail.email}
                          type="email"
                          onSave={saveCandidateField}
                        />
                      </div>
                    ) : null}

                    {/* Compensation Ladder — surfaces Current → Expected →
                        Offered → Joined CTC progression for the selected
                        candidate. Same component used by /candidates
                        view dialog so the maths stays consistent. */}
                    {showRevenue && candidateDetail ? (
                      <CompensationLadder
                        currentL={Number(candidateDetail?.current_ctc) || 0}
                        expectedL={Number(candidateDetail?.expected_ctc) || 0}
                        apps={candidateApps}
                        compact
                      />
                    ) : null}

                    {/* AI Match */}
                    <div className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                          <Sparkles className="w-3.5 h-3.5 text-teal-600" />
                          AI Match Score
                        </div>
                        {!matchScore && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleAiMatch}
                            disabled={matchLoading || !selectedApp.job_id}
                            data-testid="cc-ai-match-btn"
                            className="h-7 text-xs"
                          >
                            {matchLoading ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              'Run match'
                            )}
                          </Button>
                        )}
                      </div>
                      {matchScore && (
                        <div className="mt-2" data-testid="cc-ai-match-result">
                          <div className="flex items-center gap-2">
                            <div className="text-2xl font-bold text-teal-700">
                              {Math.round(matchScore.match_score ?? matchScore.score ?? 0)}%
                            </div>
                            <div className="text-xs text-slate-500 flex-1">
                              {matchScore.fit_summary || matchScore.summary || matchScore.recommendation || 'Score generated'}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Resume preview (collapsible) */}
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowResume((v) => !v)}
                        className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 transition"
                        data-testid="cc-toggle-resume"
                      >
                        <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5" />
                          Resume
                        </div>
                        <div className="flex items-center gap-2">
                          {candidateDetail?.resume_url || candidateDetail?.gridfs_resume_id ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Available
                            </span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                              Not uploaded
                            </span>
                          )}
                          <ChevronDown
                            className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                              showResume ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </button>
                      {showResume && (
                        <div className="px-3 pb-3">
                          {candidateDetail?.resume_url || candidateDetail?.gridfs_resume_id ? (
                            <button
                              type="button"
                              onClick={handleOpenResume}
                              disabled={resumeOpening}
                              className="inline-flex items-center gap-1 text-xs text-teal-700 hover:underline disabled:opacity-60 disabled:cursor-wait"
                              data-testid="cc-open-resume"
                            >
                              {resumeOpening ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <ExternalLink className="w-3 h-3" />
                              )}
                              {resumeOpening ? 'Opening…' : 'Open resume'}
                            </button>
                          ) : (
                            <p className="text-xs text-slate-400">No resume on file.</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Latest remarks (collapsible) */}
                    {selectedApp.remarks && (
                      <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setShowRemark((v) => !v)}
                          className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50 transition"
                          data-testid="cc-toggle-remark"
                        >
                          <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                            <StickyNote className="w-3.5 h-3.5" />
                            Latest Remark
                          </div>
                          <ChevronDown
                            className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                              showRemark ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {showRemark && (
                          <p className="px-3 pb-3 text-xs text-slate-600 whitespace-pre-wrap">
                            {selectedApp.remarks}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Bottom Tabs — Activity / Notes / Tasks / History */}
                    <div
                      className="rounded-lg border border-slate-200"
                      data-testid="cc-bottom-tabs-wrap"
                    >
                      <Tabs
                        value={bottomTab}
                        onValueChange={setBottomTab}
                        className="w-full"
                      >
                        <TabsList
                          className="w-full grid grid-cols-4 h-9 bg-slate-50 rounded-t-lg rounded-b-none border-b border-slate-200"
                          data-testid="cc-bottom-tabs"
                        >
                          <TabsTrigger
                            value="activity"
                            data-testid="cc-bottom-tab-activity"
                            className="text-[11px] gap-1"
                          >
                            <ActivityIcon className="w-3 h-3" /> Activity
                            {candidateActivity.length > 0 && (
                              <span className="text-[9px] font-mono text-slate-500">
                                {candidateActivity.length}
                              </span>
                            )}
                          </TabsTrigger>
                          <TabsTrigger
                            value="notes"
                            data-testid="cc-bottom-tab-notes"
                            className="text-[11px] gap-1"
                          >
                            <StickyNote className="w-3 h-3" /> Notes
                          </TabsTrigger>
                          <TabsTrigger
                            value="tasks"
                            data-testid="cc-bottom-tab-tasks"
                            className="text-[11px] gap-1"
                          >
                            <ListTodo className="w-3 h-3" /> Tasks
                            {candidateTasks.length > 0 && (
                              <span className="text-[9px] font-mono text-orange-600">
                                {candidateTasks.length}
                              </span>
                            )}
                          </TabsTrigger>
                          <TabsTrigger
                            value="history"
                            data-testid="cc-bottom-tab-history"
                            className="text-[11px] gap-1"
                          >
                            <Clock className="w-3 h-3" /> History
                          </TabsTrigger>
                        </TabsList>

                        {/* Activity */}
                        <TabsContent
                          value="activity"
                          className="p-3 m-0 max-h-[260px] overflow-auto"
                          data-testid="cc-tab-content-activity"
                        >
                          {candidateActivity.length === 0 ? (
                            <p className="text-[11px] text-slate-400 text-center py-4">
                              No activity yet for this candidate.
                            </p>
                          ) : (
                            <ul className="space-y-2">
                              {candidateActivity.slice(0, 20).map((it, i) => (
                                <li
                                  key={i}
                                  className="text-[11px] text-slate-600 flex gap-2"
                                >
                                  <span
                                    className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                      STAGE_BY_ID[it.stage]?.dot || 'bg-slate-400'
                                    }`}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium text-slate-700">
                                      {STAGE_BY_ID[it.stage]?.label || it.stage}
                                    </span>
                                    {it.owner_name && (
                                      <span className="inline-flex items-center gap-1 ml-1 text-slate-500">
                                        <span className="text-slate-400">·</span>
                                        <UserAvatar name={it.owner_name} size="xs" />
                                        <span>{it.owner_name}</span>
                                      </span>
                                    )}
                                    {it.date && (
                                      <span className="text-slate-400 text-[10px] ml-1">
                                        {new Date(it.date).toLocaleString(undefined, {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })}
                                      </span>
                                    )}
                                    {it.remarks && (
                                      <p className="text-slate-500 truncate">{it.remarks}</p>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </TabsContent>

                        {/* Notes */}
                        <TabsContent
                          value="notes"
                          className="p-3 m-0 space-y-2"
                          data-testid="cc-tab-content-notes"
                        >
                          <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wide text-slate-500">
                              Add a note
                            </Label>
                            <Textarea
                              value={newNote}
                              onChange={(e) => setNewNote(e.target.value)}
                              placeholder="Comment, follow-up, observation…"
                              rows={2}
                              className="text-xs"
                              data-testid="cc-note-input"
                            />
                            <div className="flex justify-end">
                              <Button
                                size="sm"
                                onClick={submitNote}
                                disabled={savingNote || !newNote.trim()}
                                className="h-7 text-xs bg-teal-700 hover:bg-teal-800"
                                data-testid="cc-note-save"
                              >
                                {savingNote ? (
                                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                ) : null}
                                Save note
                              </Button>
                            </div>
                          </div>
                          {/* Past notes: stage_history entries that have remarks */}
                          <div className="border-t border-slate-100 pt-2 max-h-[180px] overflow-auto">
                            {Array.isArray(selectedApp.stage_history) &&
                            selectedApp.stage_history.filter((h) => h.remarks).length > 0 ? (
                              <ul className="space-y-2">
                                {selectedApp.stage_history
                                  .filter((h) => h.remarks)
                                  .slice(-10)
                                  .reverse()
                                  .map((h, i) => (
                                    <li
                                      key={i}
                                      className="text-[11px] text-slate-600 border-l-2 border-slate-200 pl-2"
                                    >
                                      <p className="whitespace-pre-wrap">{h.remarks}</p>
                                      <div className="flex items-center gap-1 text-[10px] text-slate-400 mt-0.5">
                                        <UserAvatar name={h.owner_name || 'Someone'} size="xs" />
                                        <span>{h.owner_name || 'Someone'}</span>
                                        <span>·</span>
                                        <span>{STAGE_BY_ID[h.stage]?.label || h.stage}</span>
                                        {h.date && (
                                          <>
                                            <span>·</span>
                                            <span>
                                              {new Date(h.date).toLocaleString(undefined, {
                                                month: 'short',
                                                day: 'numeric',
                                              })}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    </li>
                                  ))}
                              </ul>
                            ) : (
                              <p className="text-[11px] text-slate-400 text-center py-2">
                                No notes yet.
                              </p>
                            )}
                          </div>
                        </TabsContent>

                        {/* Tasks */}
                        <TabsContent
                          value="tasks"
                          className="p-3 m-0 max-h-[260px] overflow-auto"
                          data-testid="cc-tab-content-tasks"
                        >
                          {candidateTasks.length === 0 ? (
                            <p className="text-[11px] text-slate-400 text-center py-4">
                              No pending tasks for this candidate.
                            </p>
                          ) : (
                            <ul className="space-y-2">
                              {candidateTasks.map((t) => (
                                <li
                                  key={t.id}
                                  className={`text-[11px] rounded-md border px-2 py-1.5 ${
                                    t.priority === 'high'
                                      ? 'bg-red-50 border-red-200 text-red-800'
                                      : t.priority === 'medium'
                                      ? 'bg-amber-50 border-amber-200 text-amber-800'
                                      : 'bg-slate-50 border-slate-200 text-slate-700'
                                  }`}
                                >
                                  <div className="font-medium truncate">{t.title}</div>
                                  {t.subtitle && (
                                    <div className="text-[10px] opacity-80 truncate">
                                      {t.subtitle}
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </TabsContent>

                        {/* History */}
                        <TabsContent
                          value="history"
                          className="p-3 m-0 max-h-[260px] overflow-auto"
                          data-testid="cc-tab-content-history"
                        >
                          {Array.isArray(selectedApp.stage_history) &&
                          selectedApp.stage_history.length > 0 ? (
                            <ul className="space-y-2">
                              {[...selectedApp.stage_history].reverse().map((h, i) => (
                                <li
                                  key={i}
                                  className="text-[11px] text-slate-600 flex gap-2"
                                >
                                  <span
                                    className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                      STAGE_BY_ID[h.stage]?.dot || 'bg-slate-400'
                                    }`}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium text-slate-700">
                                      {STAGE_BY_ID[h.stage]?.label || h.stage}
                                    </span>
                                    {h.owner_name && (
                                      <span className="inline-flex items-center gap-1 ml-1 text-slate-500">
                                        <span className="text-slate-400">·</span>
                                        <UserAvatar name={h.owner_name} size="xs" />
                                        <span>{h.owner_name}</span>
                                      </span>
                                    )}
                                    {h.date && (
                                      <span className="text-slate-400 text-[10px] ml-1">
                                        {new Date(h.date).toLocaleString(undefined, {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })}
                                      </span>
                                    )}
                                    {h.remarks && (
                                      <p className="text-slate-500 truncate">{h.remarks}</p>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-[11px] text-slate-400 text-center py-4">
                              No stage history.
                            </p>
                          )}
                        </TabsContent>
                      </Tabs>
                    </div>
                  </div>
                </ScrollArea>

                {/* Quick actions footer */}
                <div className="border-t p-3 grid grid-cols-2 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() =>
                      setPendingStageChange({
                        app: selectedApp,
                        targetStage: selectedApp.stage,
                      })
                    }
                    data-testid="cc-action-move"
                  >
                    <Target className="w-3.5 h-3.5 mr-1" /> Move stage
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() =>
                      setPendingStageChange({
                        app: selectedApp,
                        targetStage: 'interview_scheduled',
                      })
                    }
                    data-testid="cc-action-schedule"
                  >
                    <Calendar className="w-3.5 h-3.5 mr-1" /> Schedule
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={quickHoldFromPanel}
                    data-testid="cc-action-hold"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Hold
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
                    onClick={quickDroppedOutFromPanel}
                    data-testid="cc-action-dropped"
                    title="Candidate withdrew from the process permanently"
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Dropped
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="text-xs"
                    onClick={quickRejectFromPanel}
                    data-testid="cc-action-reject"
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </>
            )}
          </Card>
        </aside>
      </div>

      {/* ==================== Stage change confirm dialog ==================== */}
      <Dialog
        open={!!pendingStageChange}
        onOpenChange={(open) => {
          if (!open) closeStageDialog();
        }}
      >
        <DialogContent data-testid="cc-stage-dialog">
          <DialogHeader>
            <DialogTitle>
              Move {pendingStageChange?.app?.candidate_name || 'candidate'}
            </DialogTitle>
            <DialogDescription>
              Update the stage for this application. Add a remark so the team has context.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Stage</Label>
              <Select
                value={pendingStageChange?.targetStage || ''}
                onValueChange={(v) =>
                  setPendingStageChange((prev) =>
                    prev ? { ...prev, targetStage: v } : prev
                  )
                }
              >
                <SelectTrigger data-testid="cc-stage-dialog-select">
                  <SelectValue placeholder="Select target stage" />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">
                Remark
                {STAGE_BY_ID[pendingStageChange?.targetStage]?.destructive && (
                  <span className="text-red-600 ml-1">*</span>
                )}
              </Label>
              <Textarea
                value={stageRemark}
                onChange={(e) => setStageRemark(e.target.value)}
                placeholder={
                  STAGE_BY_ID[pendingStageChange?.targetStage]?.destructive
                    ? 'Reason for rejection / hold (required)…'
                    : 'Add context for the team…'
                }
                rows={3}
                data-testid="cc-stage-dialog-remark"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Client feedback (optional)</Label>
              <Textarea
                value={clientFeedback}
                onChange={(e) => setClientFeedback(e.target.value)}
                placeholder="What did the client say?"
                rows={2}
                data-testid="cc-stage-dialog-client"
              />
            </div>

            {/* Offered CTC + Joining Date — captured at JOINED so we compute Candidate CTC
                accurately. Hidden for other stages to keep the dialog clean. */}
            {pendingStageChange?.targetStage === 'joined' ? (
              <div className="space-y-3 rounded-md border border-emerald-200 bg-emerald-50/50 p-3" data-testid="cc-offered-ctc-block">
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1 text-emerald-900">
                    <span>Joining Date</span>
                    <span className="text-rose-600 font-bold">*</span>
                  </Label>
                  <Input
                    type="date"
                    value={joiningDate}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setJoiningDate(e.target.value)}
                    data-testid="cc-joining-date-input"
                    className="bg-white"
                    required
                  />
                  <p className="text-[10px] text-emerald-800/80">
                    The actual date the candidate joined. Used as the closure date in all reports.
                  </p>
                </div>
                {showRevenue && (
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1 text-emerald-900">
                    <span>Offered CTC</span>
                    <span className="text-[10px] text-emerald-700/70 font-normal">
                      · in Lakhs (LPA)
                    </span>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={offeredCtc}
                    onChange={(e) => setOfferedCtc(e.target.value)}
                    placeholder="e.g. 12.5"
                    data-testid="cc-offered-ctc-input"
                    className="bg-white"
                  />
                  {offeredCtc && !Number.isNaN(Number(offeredCtc)) && Number(offeredCtc) > 0 ? (
                    <p
                      className="text-[11px] text-emerald-900 font-medium"
                      data-testid="cc-offered-ctc-preview"
                    >
                      = ₹{Number(offeredCtc).toLocaleString('en-IN')} L (₹{(Number(offeredCtc) * 100000).toLocaleString('en-IN')})
                    </p>
                  ) : null}
                  <p className="text-[10px] text-emerald-800/80">
                    This locks the candidate CTC for this closure. Leave blank to fall back to the candidate's profile CTC.
                  </p>
                </div>
                )}
              </div>
            ) : null}

            {/* Drop-out reason — required on dropped_out transition. Whitelist
                matched on backend so the AI insight can later aggregate. */}
            {pendingStageChange?.targetStage === 'dropped_out' ? (
              <div className="space-y-3 rounded-md border border-rose-200 bg-rose-50/40 p-3" data-testid="cc-dropped-block">
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1 text-rose-900">
                    <span>Why did the candidate drop?</span>
                    <span className="text-rose-600 font-bold">*</span>
                  </Label>
                  <Select value={droppedReason} onValueChange={setDroppedReason}>
                    <SelectTrigger data-testid="cc-dropped-reason-select" className="bg-white">
                      <SelectValue placeholder="Select a reason…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="better_offer">Got better offer</SelectItem>
                      <SelectItem value="counter_offer">Counter-offer accepted</SelectItem>
                      <SelectItem value="compensation_mismatch">Compensation mismatch</SelectItem>
                      <SelectItem value="location_mismatch">Location mismatch</SelectItem>
                      <SelectItem value="personal_reasons">Personal reasons</SelectItem>
                      <SelectItem value="lost_contact">Lost contact</SelectItem>
                      <SelectItem value="other">Other (specify)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {droppedReason === 'other' ? (
                  <div className="space-y-2">
                    <Label className="text-xs text-rose-900">Specify reason</Label>
                    <Input
                      value={droppedReasonText}
                      onChange={(e) => setDroppedReasonText(e.target.value.slice(0, 200))}
                      placeholder="e.g. Family emergency"
                      data-testid="cc-dropped-reason-text"
                      className="bg-white"
                    />
                  </div>
                ) : null}
                <p className="text-[10px] text-rose-800/80">
                  Aggregated drop reasons feed into the Job Summary AI insight so managers can spot patterns (e.g. "60% of drops were counter-offers").
                </p>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeStageDialog}
              disabled={savingStage}
              data-testid="cc-stage-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmStageChange}
              disabled={
                savingStage ||
                !pendingStageChange?.targetStage ||
                pendingStageChange?.targetStage === pendingStageChange?.app?.stage
              }
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="cc-stage-dialog-confirm"
            >
              {savingStage ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Confirm move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== Best 10 candidates dialog ==================== */}
      <Dialog
        open={!!bestDialogJob}
        onOpenChange={(open) => {
          if (!open) closeBestDialog();
        }}
      >
        <DialogContent className="max-w-2xl" data-testid="cc-best-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-600" />
              Best 10 candidates — {bestDialogJob?.title || ''}
            </DialogTitle>
            <DialogDescription>
              AI-ranked candidates for this job by skills, experience and location. Tick the ones
              to add to the pipeline (Sourced stage).
            </DialogDescription>
          </DialogHeader>

          {bestLoading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Scanning candidate database…
            </div>
          ) : bestMatches.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-8">
              No matching candidates found.
            </div>
          ) : (
            <ScrollArea className="max-h-[400px] pr-2">
              <ul className="space-y-2" data-testid="cc-best-list">
                {bestMatches.map((m) => {
                  const checked = !!bestSelected[m.candidate_id];
                  return (
                    <li
                      key={m.candidate_id}
                      data-testid={`cc-best-row-${m.candidate_id}`}
                      className={`flex items-start gap-3 rounded-lg border p-3 transition cursor-pointer ${
                        checked
                          ? 'border-teal-300 bg-teal-50/50'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                      onClick={() =>
                        setBestSelected((s) => ({
                          ...s,
                          [m.candidate_id]: !s[m.candidate_id],
                        }))
                      }
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) =>
                          setBestSelected((s) => ({
                            ...s,
                            [m.candidate_id]: !!v,
                          }))
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5"
                        data-testid={`cc-best-check-${m.candidate_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-sm text-slate-900 truncate">
                            {m.candidate_name}
                          </p>
                          <Badge
                            className={`text-[10px] font-mono ${
                              m.match_score >= 80
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                : m.match_score >= 60
                                ? 'bg-amber-100 text-amber-800 border-amber-200'
                                : 'bg-slate-100 text-slate-700 border-slate-200'
                            }`}
                          >
                            {m.match_score}%
                          </Badge>
                        </div>
                        <p className="text-[11px] text-slate-500 truncate mt-0.5">
                          {m.current_designation || '—'}
                          {m.current_company ? ` · ${m.current_company}` : ''}
                          {m.experience_years ? ` · ${m.experience_years}y` : ''}
                          {m.current_location ? ` · ${m.current_location}` : ''}
                        </p>
                        <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">
                          {m.overall_assessment}
                        </p>
                        {Array.isArray(m.matching_skills) && m.matching_skills.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {m.matching_skills.slice(0, 5).map((s) => (
                              <span
                                key={s}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                              >
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </ScrollArea>
          )}

          <DialogFooter>
            <span
              className="text-xs text-slate-500 mr-auto"
              data-testid="cc-best-selected-count"
            >
              {Object.values(bestSelected).filter(Boolean).length} selected
            </span>
            <Button
              variant="outline"
              onClick={closeBestDialog}
              disabled={bestSubmitting}
              data-testid="cc-best-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitBestShortlist}
              disabled={
                bestSubmitting ||
                bestLoading ||
                Object.values(bestSelected).filter(Boolean).length === 0
              }
              className="bg-violet-700 hover:bg-violet-800"
              data-testid="cc-best-confirm"
            >
              {bestSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Add to pipeline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Drill-down dialog: clicking a KPI tile shows the underlying records ============ */}
      <Dialog
        open={drillMetric !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDrillMetric(null);
            setDrillItems([]);
            setDrillSummary({});
            setDrillDropConfirmId(null);
          }
        }}
      >
        <DialogContent
          className="max-w-2xl max-h-[80vh] flex flex-col p-0 gap-0"
          data-testid="cc-drill-dialog"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
            <DialogTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              {drillMetric ? DRILL_TITLES[drillMetric] : ''}
              {!drillLoading && drillItems.length > 0 ? (
                <Badge variant="secondary" className="text-xs" data-testid="cc-drill-count">
                  {drillItems.length}
                </Badge>
              ) : null}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {drillMetric === 'pending'
                ? 'Auto-generated tasks based on stale screenings, pending offers and today\u2019s interviews. Click a row to open that candidate.'
                : drillMetric === 'pending_paperwork'
                ? 'Candidates moved to Offered but missing an offer letter date. Longest-waiting first \u2014 click a row to record paperwork.'
                : drillMetric === 'pending_joining'
                ? 'Offers with a past joining date but the candidate was never marked as Joined. Click a row to confirm or chase.'
                : drillMetric === 'tat'
                ? 'Joined candidates with their turnaround time (sourced \u2192 joined). Click a row to open that candidate.'
                : drillMetric === 'revenue'
                ? `Estimated placement fee at ${drillSummary.placement_fee_pct ?? 8.33}% of expected/current CTC. Click a row to open that candidate.`
                : 'Click any row to open the candidate in this Command Center.'}
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto px-5 py-3 flex-1" data-testid="cc-drill-list">
            {drillLoading ? (
              <div className="flex items-center justify-center py-12 text-slate-400" data-testid="cc-drill-loading">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                <span className="text-sm">Loading…</span>
              </div>
            ) : drillItems.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="cc-drill-empty">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm font-medium">Nothing here yet</p>
                <p className="text-xs text-slate-400 mt-1">
                  {drillMetric === 'submissions'
                    ? 'No candidates have been submitted today.'
                    : drillMetric === 'interviews'
                    ? 'No interviews are scheduled for today.'
                    : drillMetric === 'closures'
                    ? 'No closures have been recorded this month yet.'
                    : drillMetric === 'tat'
                    ? 'No closed-won candidates yet \u2014 TAT will populate once you join your first candidate.'
                    : drillMetric === 'pending'
                    ? 'No pending tasks \u2014 your pipeline is healthy.'
                    : drillMetric === 'pending_paperwork'
                    ? 'All released offers have an offer letter date on file.'
                    : drillMetric === 'pending_joining'
                    ? 'No overdue joinings \u2014 every offer with a past joining date has been confirmed.'
                    : 'No records to show.'}
                </p>
              </div>
            ) : (
              <ul className="space-y-1.5">
                {drillItems.map((it, idx) => {
                  const showJoinedAction = drillMetric === 'pending_joining';
                  const savingThis = drillSavingId === it.application_id;
                  return (
                  <li key={(it.application_id || idx) + '-' + idx}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleDrillRowClick(it)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDrillRowClick(it); } }}
                      data-testid={`cc-drill-row-${idx}`}
                      className="w-full text-left rounded-md border border-slate-200 hover:border-teal-300 hover:bg-teal-50/40 transition px-3 py-2.5 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-300"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5 text-slate-500 group-hover:bg-teal-100 group-hover:text-teal-600">
                          <User className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900 truncate">
                              {it.candidate_name || 'Unnamed'}
                            </span>
                            {drillMetric === 'pending' && it.priority ? (
                              <Badge
                                variant="secondary"
                                className={`text-[10px] uppercase ${
                                  it.priority === 'high'
                                    ? 'bg-rose-100 text-rose-700'
                                    : it.priority === 'medium'
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-slate-100 text-slate-600'
                                }`}
                              >
                                {it.priority}
                              </Badge>
                            ) : null}
                            {it.value_label ? (
                              <Badge variant="outline" className="text-[10px] ml-auto">
                                {it.value_label}
                              </Badge>
                            ) : null}
                          </div>
                          <div className="text-xs text-slate-500 truncate mt-0.5">
                            {it.job_title || '—'}
                            {it.client_name ? ` \u00b7 ${it.client_name}` : ''}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                            {it.stage ? (
                              <span className="uppercase tracking-wide font-medium">{it.stage.replace(/_/g, ' ')}</span>
                            ) : null}
                            {it.date ? (
                              <span>
                                {new Date(it.date).toLocaleString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        {showJoinedAction ? (
                          drillDropConfirmId === it.application_id ? (
                            <div className="flex items-center gap-1.5 flex-shrink-0 self-center" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="sm"
                                type="button"
                                onClick={(e) => markAsDroppedFromDrill(it, e)}
                                disabled={savingThis || !!drillSavingId}
                                data-testid={`cc-drill-confirm-drop-${idx}`}
                                className="h-7 px-2.5 text-[11px] font-semibold bg-rose-600 hover:bg-rose-700 text-white"
                              >
                                {savingThis ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Dropping
                                  </>
                                ) : (
                                  'Confirm drop'
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setDrillDropConfirmId(null); }}
                                disabled={savingThis}
                                data-testid={`cc-drill-cancel-drop-${idx}`}
                                className="h-7 px-2 text-[11px] text-slate-600 hover:text-slate-900"
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 flex-shrink-0 self-center" onClick={(e) => e.stopPropagation()}>
                              <Button
                                size="sm"
                                variant="default"
                                type="button"
                                onClick={(e) => markAsJoinedFromDrill(it, e)}
                                disabled={savingThis || !!drillSavingId}
                                data-testid={`cc-drill-mark-joined-${idx}`}
                                className="h-7 px-2.5 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                              >
                                {savingThis ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Saving
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Mark joined
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setDrillDropConfirmId(it.application_id); }}
                                disabled={savingThis || !!drillSavingId}
                                data-testid={`cc-drill-mark-dropped-${idx}`}
                                className="h-7 px-2.5 text-[11px] font-semibold border-rose-200 text-rose-700 hover:bg-rose-50 hover:border-rose-300"
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Mark dropped
                              </Button>
                            </div>
                          )
                        ) : (
                          <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-teal-500 flex-shrink-0 mt-1" />
                        )}
                      </div>
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>

          <DialogFooter className="px-5 py-3 border-t border-slate-100 bg-slate-50/50">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDrillMetric(null)}
              data-testid="cc-drill-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Bulk action confirmation dialog ============ */}
      <Dialog
        open={bulkAction !== null}
        onOpenChange={(open) => {
          if (!open && !bulkSubmitting) {
            setBulkAction(null);
            setBulkRemarks('');
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="cc-bulk-dialog">
          <DialogHeader>
            <DialogTitle>
              {bulkAction?.mode === 'reject'
                ? `Reject ${bulkSelected.size} candidate${bulkSelected.size === 1 ? '' : 's'}?`
                : `Move ${bulkSelected.size} candidate${bulkSelected.size === 1 ? '' : 's'} to ${
                    STAGE_BY_ID[bulkAction?.target_stage]?.label || bulkAction?.target_stage || ''
                  }?`}
            </DialogTitle>
            <DialogDescription>
              {bulkAction?.mode === 'reject'
                ? 'This will move every selected candidate to Rejected and add the reason to their stage history. A rejection reason is required.'
                : 'A note is optional but recommended — it will be saved on every candidate\u2019s stage history.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 mt-2">
            <Label htmlFor="cc-bulk-remarks" className="text-xs text-slate-600">
              {bulkAction?.mode === 'reject' ? 'Reason for rejection *' : 'Note (optional)'}
            </Label>
            <Textarea
              id="cc-bulk-remarks"
              data-testid="cc-bulk-remarks"
              placeholder={
                bulkAction?.mode === 'reject'
                  ? 'e.g. Did not meet experience bar'
                  : 'e.g. Profiles validated by team lead'
              }
              value={bulkRemarks}
              onChange={(e) => setBulkRemarks(e.target.value)}
              rows={3}
              className="resize-none text-sm"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setBulkAction(null);
                setBulkRemarks('');
              }}
              disabled={bulkSubmitting}
              data-testid="cc-bulk-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant={bulkAction?.mode === 'reject' ? 'destructive' : 'default'}
              onClick={submitBulkAction}
              disabled={bulkSubmitting || (bulkAction?.mode === 'reject' && !bulkRemarks.trim())}
              data-testid="cc-bulk-confirm"
            >
              {bulkSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {bulkAction?.mode === 'reject' ? 'Reject all' : 'Move all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default CommandCenter;
